import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutInfo, faqData } from '@/mocks/comics';

export default function About() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-6">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]} />

            <div className="mt-8">
              <section className="text-center mb-14">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">关于土豪漫画网</h1>
                <p className="mt-4 text-foreground-500 text-base max-w-2xl mx-auto leading-relaxed">
                  {aboutInfo.brandIntro}
                </p>
              </section>

              <section className="mb-14">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl mb-4">
                      <i className="ri-eye-line text-2xl text-primary-600"></i>
                    </div>
                    <h3 className="font-heading text-lg text-foreground-950 mb-2">我们的愿景</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.vision}</p>
                  </div>
                  <div className="bg-background-50 rounded-xl border border-background-200/60 p-6 md:p-8">
                    <div className="w-12 h-12 flex items-center justify-center bg-accent-100 rounded-xl mb-4">
                      <i className="ri-flag-line text-2xl text-accent-600"></i>
                    </div>
                    <h3 className="font-heading text-lg text-foreground-950 mb-2">我们的使命</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{aboutInfo.mission}</p>
                  </div>
                </div>
              </section>

              <section className="mb-14">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">发展历程</h2>
                  <p className="text-foreground-500 text-sm mt-2">从创立至今，我们一直在成长</p>
                </div>
                <div className="relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200 md:-translate-x-0.5"></div>
                  <div className="flex flex-col gap-6">
                    {aboutInfo.milestones.map((milestone, index) => (
                      <div key={milestone.year} className={`flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                        <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                          {index % 2 === 0 ? (
                            <div className="bg-background-50 border border-background-200/60 rounded-lg p-4 inline-block">
                              <p className="text-sm text-foreground-600">{milestone.event}</p>
                            </div>
                          ) : null}
                        </div>
                        <div className="relative z-10 flex-shrink-0">
                          <div className="w-8 h-8 flex items-center justify-center bg-primary-500 text-background-50 rounded-full text-xs font-bold">
                            {milestone.year.slice(2)}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="md:hidden bg-background-50 border border-background-200/60 rounded-lg p-4 mb-2">
                            <p className="text-sm text-foreground-600">{milestone.event}</p>
                          </div>
                          <span className="text-sm font-bold text-primary-500">{milestone.year}</span>
                          {index % 2 !== 0 ? (
                            <div className="hidden md:block bg-background-50 border border-background-200/60 rounded-lg p-4 mt-2">
                              <p className="text-sm text-foreground-600">{milestone.event}</p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mb-14">
                <div className="text-center mb-8">
                  <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">核心优势</h2>
                  <p className="text-foreground-500 text-sm mt-2">为什么选择土豪漫画网</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {aboutInfo.features.map((feature, index) => (
                    <div key={index} className="bg-background-50 rounded-xl border border-background-200/60 p-6 text-center hover:border-primary-300 transition-colors">
                      <div className="w-14 h-14 flex items-center justify-center bg-background-100 rounded-xl mx-auto mb-4">
                        <i className={`${feature.icon} text-2xl text-primary-500`}></i>
                      </div>
                      <h4 className="font-heading text-base text-foreground-950 mb-2">{feature.title}</h4>
                      <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">联系我们</h2>
                    <div className="bg-background-50 rounded-xl border border-background-200/60 p-6 space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-background-100 rounded-lg flex-shrink-0">
                          <i className="ri-mail-line text-lg text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">邮箱</p>
                          <p className="text-sm text-foreground-950">{aboutInfo.contact.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-background-100 rounded-lg flex-shrink-0">
                          <i className="ri-phone-line text-lg text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">电话</p>
                          <p className="text-sm text-foreground-950">{aboutInfo.contact.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-background-100 rounded-lg flex-shrink-0">
                          <i className="ri-map-pin-line text-lg text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">地址</p>
                          <p className="text-sm text-foreground-950">{aboutInfo.contact.address}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-background-100 rounded-lg flex-shrink-0">
                          <i className="ri-time-line text-lg text-primary-500"></i>
                        </div>
                        <div>
                          <p className="text-xs text-foreground-400">工作时间</p>
                          <p className="text-sm text-foreground-950">{aboutInfo.contact.workingHours}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-6">常见问题</h2>
                    <div className="space-y-3">
                      {faqData.map((faq, index) => (
                        <div key={index} className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
                          <button
                            onClick={() => setOpenFaq(openFaq === index ? null : index)}
                            className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                          >
                            <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
                            <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}></i>
                          </button>
                          {openFaq === index && (
                            <div className="px-4 pb-4 animate-[fadeIn_0.2s_ease]">
                              <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              <section className="text-center py-10">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">加入我们的社区</h2>
                <p className="mt-3 text-foreground-500 text-sm max-w-md mx-auto">
                  与千万漫画爱好者一起，享受最优质的漫画阅读体验
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                  <Link
                    to="/category/hanman"
                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始免费阅读
                  </Link>
                  <a
                    href={`mailto:${aboutInfo.contact.email}`}
                    className="px-6 py-3 border border-background-300 text-foreground-700 hover:text-foreground-950 hover:border-foreground-400 font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    联系我们
                  </a>
                </div>
              </section>
            </div>
          </div>
        </div>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': faqData.map((faq) => ({
            '@type': 'Question',
            'name': faq.question,
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': faq.answer,
            },
          })),
        }) }} />
      </main>
      <Footer />
    </>
  );
}